import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { SubscriberService } from '@services';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';

import { Options } from 'select2';
import { parse } from "tinyduration";
import { every, split } from 'lodash';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
})
export class TimePickerComponent extends CustomFormComponent implements OnInit {
  @Output() onChanged: EventEmitter<any> = new EventEmitter<any>();

  @Input() duration8601 = '';
  @Input() time = '00:00';
  @Input() isDuration = false;
  @Input() min = '00:00';
  @Input() max = '23:59';
  @Input() label = this.translate.instant('SHARED.Time');
  @Input() tooltip = '';
  @Input() noLabel = false;
  @Input() required = false;
  @Input() longDuration = false;
  @Input() default;

  public format = 'h:mm A';
  public instanceID: string;
  public months = this.getArrayOfOptions(13, false);
  public weeks = this.getArrayOfOptions(53, false);
  public days = this.getArrayOfOptions(8, false);

  public selectedMonths = '0';
  public selectedWeeks = '0';
  public selectedDays = '0';
  public selectedHour: number | string = 0;
  public selectedMinute: number | string = 0;

  public selectOptions: Options = {
    width: '4em',
    minimumResultsForSearch: 100,
  };
  private isInitializing = true;

  constructor(
    private subscriberService: SubscriberService,
    private translate: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    this.initTimePicker();
    this.isInitializing = false;
  }

  public emit8601(): void {
    const isZero = (...values: (string | number)[]) => every(values, val => !+val);
    const hour = this.padValue(this.selectedHour);
    const minute = this.padValue(this.selectedMinute);

    if (this.longDuration) {
      const theTime = `P${this.selectedMonths}M${this.selectedWeeks}W${this.selectedDays}DT${hour}H${minute}M`;
      const allZero = isZero(this.selectedMonths, this.selectedWeeks, this.selectedDays, this.selectedHour, this.selectedMinute);

      this.updateFormValue(this.isFormValueInvalid() && allZero ? '' : theTime);
    } else {
      if (this.min === '00:00' && isZero(this.selectedHour, this.selectedMinute)) {
        this.updateFormValue('00:00');
        return;
      }

      const allZero = isZero(this.selectedHour, this.selectedMinute);
      this.updateFormValue(this.isFormValueInvalid() && allZero ? '' : `${hour}:${minute}`);
    }
  }

  public emitChanges(theTime: string): void {
    this.updateFormValue(this.isFormValueInvalid() && !theTime ? '' : theTime);
  }

  private isFormValueInvalid(): boolean {
    return !this.formValue || this.required;
  }

  public onChangedMonths(event) {
    this.selectedMonths = event.value;
    this.emit8601();
  }

  public onChangedWeeks(event) {
    this.selectedWeeks = event.value;
    this.emit8601();
  }

  public onChangedDays(event) {
    this.selectedDays = event.value;
    this.emit8601();
  }

  // Numeric input handlers for hours/minutes
  public onHourInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.selectedHour = parseInt(value, 10) || 0;
    this.emit8601();
  }

  public onMinuteInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.selectedMinute = parseInt(value, 10) || 0;
    this.emit8601();
  }

  public validateHour(): void {
    let hour = typeof this.selectedHour === 'string' ? parseInt(this.selectedHour, 10) : this.selectedHour;
    if (isNaN(hour) || hour < 0) {
      hour = 0;
    }
    // For non-longDuration mode, cap at 23
    if (!this.longDuration && hour > 23) {
      hour = 23;
    }
    this.selectedHour = hour;
    this.emit8601();
  }

  public validateMinute(): void {
    let minute = typeof this.selectedMinute === 'string' ? parseInt(this.selectedMinute, 10) : this.selectedMinute;
    if (isNaN(minute) || minute < 0) {
      minute = 0;
    } else if (minute > 59) {
      minute = 59;
    }
    this.selectedMinute = minute;
    this.emit8601();
  }

  // Legacy handlers for select2 (kept for backwards compatibility if needed)
  public onChangedHour(event) {
    this.selectedHour = parseInt(event.value, 10) || 0;
    this.emit8601();
  }

  public onChangedMinute(event) {
    this.selectedMinute = parseInt(event.value, 10) || 0;
    this.emit8601();
  }

  public onChangedValue(event) {
    this.emitChanges(event.target.value);
  }

  private initTimePicker(): void {
    if (!this.isDuration) {
      // use the subscriber preference?
      const subConfig = this.subscriberService.getUnits('time');
      if (subConfig === '24h') {
        this.format = 'HH:mm';
      } else {
        this.format = 'h:mm A';
      }
    }
    const initialValue = this.formValue ?? this.default ?? '' ;
    if (initialValue !== '') {
      if (this.longDuration) {
        try {
          const myDuration = parse(this.formValue);
          this.selectedMonths = (myDuration.months ?? 0).toString();
          this.selectedWeeks = (myDuration.weeks ?? 0).toString();
          this.selectedDays = (myDuration.days ?? 0).toString();
          this.selectedHour = myDuration.hours ?? 0;
          this.selectedMinute = myDuration.minutes ?? 0;
        } catch (error) {
          // Invalid duration format - reset to default values
          console.warn('Invalid duration format:', this.formValue, error);
          this.selectedMonths = '0';
          this.selectedWeeks = '0';
          this.selectedDays = '0';
          this.selectedHour = 0;
          this.selectedMinute = 0;
        }
      } else {
        // parse the initial value
        this.time = String(initialValue);
        if (this.time.includes(':')) {
          if (this.time.includes('"')) {
            this.time = this.time.replace(/"/g, '');
          }
          const t = split(this.time, ':');
          this.selectedHour = parseInt(t[0], 10) || 0;
          this.selectedMinute = parseInt(t[1], 10) || 0;
        }
      }
      this.emit8601();
    } else {
      // force initialization
      this.emit8601();
    }
  }

  private padValue(value: number | string): string {
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    return num < 10 ? `0${num}` : `${num}`;
  }

  private getArrayOfOptions(length: number, zeroFill: boolean = true) {
    return Array.from({ length }, (value, index) => {
      if (zeroFill) {
        return ( index > 9 ? { value: index, description: index} : { value: `0${index}`, description:`0${index}` } ) ;
      } else {
        // no leading zero
        return ( index > 9 ? { value: index, description: index} : { value: index, description:`0${index}` } ) ;
      }
    });
  }

   private updateFormValue(value: any): void {
    if (!this.isInitializing) {
      this.formValue = value;
    }
  }
}
